<template>
  <div class="login-form">
    <v-snackbar
      v-model="show"
      top
      right
      :color="notification.color"
    >
      {{ notification.message }}

      <template #action="{ attrs }">
        <v-btn
          color="white"
          icon
          v-bind="attrs"
          @click="show = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
    <v-form
      ref="form"
      @submit.prevent="submit"
    >
      <v-card
        class="rounded-0"
        style="box-shadow: 2px 2px 4px 0px #2a3244 !important;"
      >
        <v-col class="col-12 text-center text-uppercase font-weight-bold btn-bg-gunmetal py-5">
          {{ productName }}
        </v-col>
      </v-card>
      <v-card
        class="mx-auto pa-5"
        outlined
        tile
        elevation="6"
      >
        <v-card-title class="my-1 py-7">
          <v-img
            contain
            height="38px"
            position="center"
            :src="logoUrl"
          />
        </v-card-title>

        <v-card-text class="mt-6 pb-0">
          <v-alert
            v-if="formError"
            color="red"
            type="error"
          >
            {{ formError }}
          </v-alert>
          <v-text-field
            v-model="username"
            prepend-inner-icon="mdi-account-outline"
            label="Username"
            variant="outlined"
            color="primary"
            required
            :error-messages="usernameErrors"
            @input="v$.username.$touch()"
            @blur="v$.username.$touch()"
          />
          <!-- ./username -->

          <v-text-field
            v-model="password"
            prepend-inner-icon="mdi-lock-outline"
            label="Password"
            variant="outlined"
            color="primary"
            type="password"
            required
            :error-messages="passwordErrors"
            @input="v$.password.$touch()"
            @blur="v$.password.$touch()"
          />
          <!-- ./password  -->
        </v-card-text>

        <v-card-actions class="mb-3 px-4">
          <v-btn
            type="submit"
            color="primary"
            size="large"
            block
            class="login-btn"
            :loading="formProcessing"
          >
            <v-icon>mdi-login-variant</v-icon>
            Login
          </v-btn>
        </v-card-actions>
        <p class="text-right">
          <router-link :to="{ name: 'forgot-password' }">
            Forgot your password?
          </router-link>
        </p>
        <div class="text-center">
          <span class="text-caption font-weight-thin text-center">{{ version }}</span>
        </div>
      </v-card>
    </v-form>
    <two-factor-auth
      :dialog="twoFactorAuthDialog"
      @failed-mfa="resetForm"
    />
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import {
  mapActions,
  mapGetters,
  mapState,
} from 'vuex';
import logoUrl from '@/assets/hyphen/logo.png';

import TwoFactorAuth from './TwoFactorAuth.vue';

export default {
  components: { TwoFactorAuth },
  setup() { return { v$: useVuelidate() }; },
  data() {
    return {
      logoUrl,
      show: false,
      username: '',
      password: '',
      formProcessing: false,
      formError: '',
      twoFactorAuthDialog: false,
    };
  },
  computed: {
    ...mapState('notification', ['notification']),
    ...mapGetters('app', ['assetPath', 'version', 'name']),
    ...mapGetters('settings', ['productName']),
    $v() {
      return this.v$;
    },
    usernameErrors() {
      const errors = [];

      if (!this.v$.username.$dirty) {
        return errors;
      }
      if (!this.v$.username.required) {
        errors.push('Username is required.');
      }

      return errors;
    },
    passwordErrors() {
      const errors = [];

      if (!this.v$.password.$dirty) {
        return errors;
      }
      if (!this.v$.password.required) {
        errors.push('Password is required.');
      }

      return errors;
    },
  },
  watch: {
    notification(value) {
      this.show = !!(value && value.message);
    },
  },
  validations: {
    username: { required },
    password: { required },
  },

  methods: {
    ...mapActions('currentUser', ['logUserIn']),
    async submit() {
      this.v$.$touch();

      if (this.v$.$invalid) {
        return;
      }

      this.setFormState();
      try {
        await this.$axios.get('/sanctum/csrf-cookie');
        const res = await this.$axios.post('/login', { username: this.username, password: this.password });

        if (res.data.two_factor === true) {
          // pop-up modal for 2fa login
          this.twoFactorAuthDialog = true;
          this.setFormState(false, '');
        } else if (res.data.two_factor === false
          && res.data.user.permission
          && res.data.user.permission.length
        ) {
          this.logUserIn(res.data.user);
          this.$router.push({ name: 'dashboard-home' });
        } else {
          this.setFormState(false, 'Authentication error');
        }
      } catch (error) {
        this.setFormState(
          false,
          error.response.data.errors.username.pop() ?? error.response.data.message,
        );
      }
    },
    setFormState(processing = true, message = '') {
      this.formProcessing = processing;
      this.formError = message;
    },
    resetForm() {
      this.v$.$reset();
      this.username = '';
      this.password = '';
      this.twoFactorAuthDialog = false;
    },
  },
};
</script>

<style>
.login-form {
  width: 480px;
}
.login-form .v-card {
  box-shadow: 2px 2px 4px 0px grey !important;
}
.login-form .login-btn {
  background-color: #205e86 !important;
  color: white !important;
  height: 3.5rem !important;
  font-size: 1.1rem !important;
  font-weight: 800 !important;
}
.login-form .login-btn .v-icon {
  color: white !important;
  font-size: 2.2rem !important;
  font-weight: 400 !important;
}
.login-form .v-field__prepend-inner {
  padding-top: 0 !important;
}
.login-form .v-field__prepend-inner .v-icon {
  color: #205e86 !important;
  font-size: 2rem !important;
  opacity: 1 !important;
}
.login-form .v-field .v-label {
  padding: 0.5rem 1rem;
  height: auto;
}
.login-form .v-field--focused .v-label {
  color: #205e86 !important;
}
.login-form .v-field__input {
  padding: 0rem 1rem !important;
  min-height: 50px !important;
}
.login-form .v-field {
  background-color: #e9eaec !important;
  border-radius: 4px;
  min-height: 60px;
}
.login-form .v-field--variant-outlined .v-field__outline {
  --v-field-border-opacity: 1;
}
.login-form .v-field--focused .v-field__outline {
  color: #205e86 !important;
  --v-field-border-width: 2px;
}
</style>
